import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '../context/ThemeContext';

function Community() {
  const { theme } = useTheme();
  const tokens = theme.tokens;
  const tc = tokens.colors;

  const cardStyle = {
    background: `color-mix(in srgb, ${tc.surface} 75%, transparent)`,
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(6px)',
    border: `1px solid ${tc.gridLine}`,
    borderRadius: 14,
    padding: '32px 36px',
    marginTop: 24,
  };

  const h1Style = {
    fontFamily: tokens.fonts.display,
    fontSize: 'clamp(24px, 5vw, 32px)',
    fontWeight: 600,
    color: tc.ink,
    marginBottom: 20,
    lineHeight: 1.2,
  };

  const h2Style = {
    fontFamily: tokens.fonts.display,
    fontSize: 20,
    fontWeight: 600,
    color: tc.ink,
    marginTop: 28,
    marginBottom: 10,
  };

  const linkStyle = { color: tc.accent };
  const dark = tokens.mode === 'dark';

  return (
    <div className="container mx-auto px-4 sm:px-8 pb-12" style={{ color: tc.muted, fontFamily: tokens.fonts.ui, textAlign: 'left' }}>
      <Helmet>
        <title>Atlanta Tech Community & Organizations | ATLTech.events</title>
        <meta name="description" content="Meet the organizations, sponsors, and groups behind Atlanta's tech community — meetups, incubators, and professional associations featured on ATLTech.events." />
      </Helmet>
      <div style={cardStyle}>
        <h1 style={h1Style}>Atlanta Tech Community</h1>

        <div className="space-y-4">
          <p>
            Atlanta's tech community is one of the strongest you'll find. From collaboration, incubators, investors, and the support community, there's nothing you can't find in Atlanta.
          </p>
          <p>
            We appreciate the support of our community sponsors in making ATLTech.events possible. Want to be a community sponsor? Contact us on <a href="https://www.linkedin.com/company/atl-tech-events" style={linkStyle} className="hover:underline" target="_blank" rel="noopener noreferrer">LinkedIn</a> to discuss.
          </p>

          <h2 style={h2Style}>Community Partners</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-4">
              <a href="https://engage.isaca.org/atlantachapter/home" target="_blank" rel="noopener noreferrer">
                <img src="/logos/ISACA_logo_Atlanta_RGB.png" className="w-48" alt="ISACA Atlanta"
                  style={dark ? { filter: 'brightness(0) invert(1)', opacity: 0.85 } : undefined} />
              </a>
            </div>
          </div>

          <h2 style={h2Style}>In-Kind Partners</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-4">
              <a href="https://www.atl.tech/" target="_blank" rel="noopener noreferrer">
                <img src="/logos/ATW_full_black.png" className="w-48" alt="Atlanta Tech Week"
                  style={dark ? { filter: 'brightness(0) invert(1)', opacity: 0.85 } : undefined} />
              </a>
            </div>
          </div>

          <h2 style={h2Style}>Additional Technology Organizations</h2>
          <p style={{ fontSize: 13, color: tc.faint }}>(In alphabetical order)</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><a href="https://angelatlanta.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Atlanta Technology Angels</a></li>
            <li><a href="https://www.atdc.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">ATDC</a></li>
            <li><a href="https://www.atlantatechpark.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Atlanta Tech Park</a></li>
            <li><a href="https://atpconnect.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Atlanta Technology Professionals</a></li>
            <li><a href="https://www.hdiatlanta.org/cpages/home" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">HDI Atlanta</a></li>
            <li><a href="https://www.hypepotamus.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Hypepotamus</a></li>
            <li><a href="https://www.issaatl.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">ISSA Metro Atlanta Chapter</a></li>
            <li><a href="https://www.russellcenter.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Russell Center</a></li>
            <li><a href="https://chapter.simnet.org/atlanta/home" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">SIM Atlanta</a></li>
            <li><a href="https://startupatlanta.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Startup Atlanta</a></li>
            <li><a href="https://techalpharetta.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Tech Alpharetta</a></li>
            <li><a href="https://www.tagonline.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Technology Association of Georgia (TAG)</a></li>
            <li><a href="https://techsmartseniors.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">TechSmart for Seniors</a></li>
            <li><a href="https://www.techstars.com/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Techstars</a></li>
            <li><a href="https://tieatlanta.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">TiE Atlanta</a></li>
            <li><a href="https://mywit.org/" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">Women in Technology</a></li>
          </ul>

          <p className="mt-8">Who are we missing? Let us know on <a href="https://www.linkedin.com/company/atl-tech-events" target="_blank" rel="noopener noreferrer" style={linkStyle} className="hover:underline">LinkedIn</a>!</p>
        </div>
      </div>
    </div>
  );
}

export default Community;
